import { useEffect } from "react";
import { PERSISTENCE } from "@/config/designer";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { saveDesign } from "@/core/persistence/storage";
import { useDesignerStore } from "@/state/designerStore";

/** Mirror the engine's change events into the UI store. */
export function useEngineBinding(engine: DesignerEngine | null): void {
  useEffect(() => {
    if (!engine) return;
    const { bumpRevision, setHistoryState, setBuildingHoles } = useDesignerStore.getState();

    const syncHistory = (): void => setHistoryState(engine.history.canUndo, engine.history.canRedo);
    syncHistory();

    const unsubscribe = [
      engine.on("geometry", bumpRevision),
      engine.on("fixings", bumpRevision),
      engine.on("finish", bumpRevision),
      engine.on("history", syncHistory),
      engine.on("solid", () => setBuildingHoles(engine.solidBusy)),
      engine.history.subscribe(syncHistory),
    ];
    return () => {
      for (const off of unsubscribe) off();
    };
  }, [engine]);
}

/**
 * Rebuild the viewport mesh with the fixing holes actually cut, shortly after
 * the user stops editing. Debounced so a stroke never pays for it mid-drag.
 */
export function useSolidPreview(engine: DesignerEngine | null): void {
  const revision = useDesignerStore((s) => s.revision);

  useEffect(() => {
    if (!engine || engine.fixings.length === 0) return;
    const timer = window.setTimeout(() => {
      void engine.refreshSolid();
    }, 220);
    return () => window.clearTimeout(timer);
  }, [engine, revision]);
}

/** Autosave to IndexedDB after a quiet period. */
export function useAutosave(engine: DesignerEngine | null): void {
  const revision = useDesignerStore((s) => s.revision);

  useEffect(() => {
    if (!engine) return;
    const timer = window.setTimeout(() => {
      void saveDesign(engine.toSaved());
    }, PERSISTENCE.debounceMs);
    return () => window.clearTimeout(timer);
  }, [engine, revision]);

  // One last save on the way out, so closing the tab mid-session is safe.
  useEffect(() => {
    if (!engine) return;
    const onHide = (): void => {
      if (document.visibilityState === "hidden") void saveDesign(engine.toSaved());
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [engine]);
}
